import { useEffect, useState } from "react";

import { PalAutoComplete, PalLink, PalTypography } from "@palamar/fe-library";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { Alert, Box, Grid } from "@mui/material";

import { ReactComponent as ShopifyIcon } from "~assets/images/onboarding/shopify.svg";
import { useDebouncedEffect } from "~common/hooks/useDebounceEffect";
import useQueryParams from "~common/hooks/useQueryParams";
import AlertHelper from "~helpers/AlertHelper";
import { getShopifyIntegrations, postShopifyIntegrationCallback } from "~store/settings/eCommerce/actions";
import { selectShopifyIntegrations } from "~store/settings/eCommerce/selectors";

import { StepContainer } from "../CommonComponents";
let isFetchedDone = false;
function StepShopifyIntegration({ formik, setFieldValue, stepKey, disableBottomActions }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const shopifyIntegrations = useSelector(selectShopifyIntegrations);
  const [integrationOptions, setIntegrationOptions] = useState([]);
  // const location = useLocation();
  // const shopifyIntegrationParams = location?.state?.shopifyIntegrationParams;

  const { shopifyCode } = useQueryParams();
  const values = formik.values[stepKey];

  useDebouncedEffect(
    () => {
      if (isFetchedDone) return;
      const abortController = new AbortController();
      const signal = abortController.signal;

      dispatch(
        postShopifyIntegrationCallback(
          {
            code: shopifyCode,
          },
          {
            noAlert: true,
            signal,
          }
        )
      )
        .then((response) => {
          if (signal.aborted) return;
          isFetchedDone = true;
          if (response?.status === "success") {
            dispatch(getShopifyIntegrations());
          } else {
            AlertHelper.showError(t("network.serverError"), response?.message);
            disableBottomActions?.(true);
          }
        })
        .catch((e) => {
          if (signal.aborted) return;

          isFetchedDone = true;
          disableBottomActions?.(true);
          setFieldValue("callback_done", false);
          AlertHelper.showError(t("network.serverError"), e.response?.data || e.response?.message || e.message);
        });
      return () => {
        abortController.abort();
      };
    },
    [dispatch, t],
    250
  );

  useEffect(() => {
    if (shopifyIntegrations?.length > 0) {
      const options = shopifyIntegrations.map((integration) => ({
        label: integration.shop_name,
        value: integration.id,
        disabled: !!integration.project,
      }));
      setIntegrationOptions(options);
    }
  }, [shopifyIntegrations]);

  const handleChangeIntegration = (e, value) => {
    setFieldValue("shopify_integration", value?.value || null);
  };

  return (
    <StepContainer>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {!formik.values[stepKey]?.integrated && !shopifyIntegrations?.length && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("dashboard.onboarding.noShopifyIntegration")}
            </Alert>
          )}
          {/* {!formik.values[stepKey]?.integrated && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t("dashboard.onboarding.shopifyIntegrationError")}
            </Alert>
          )} */}
          {formik.values[stepKey]?.integrated && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t("dashboard.onboarding.shopifyIntegrationConnected")}
            </Alert>
          )}
        </Grid>
        <Grid item mb={2} xs={12}>
          <Box
            sx={{
              maxWidth: 225,
              width: "100%",
            }}
          >
            <ShopifyIcon />
          </Box>
        </Grid>
        <Grid item mb={2} xs={12}>
          <PalTypography color="textPrimary" fontWeight={600} variant="h5">
            {t("dashboard.onboarding.selectShopifyIntegrationTitle")}
          </PalTypography>
          <PalTypography gutterBottom variant="body1">
            {t("dashboard.onboarding.selectShopifyIntegrationDescription")}
          </PalTypography>
          <PalTypography gutterBottom variant="subtitle2">
            {t("dashboard.onboarding.addShopifyIntegrationDescription")}
            <PalLink href="https://apps.shopify.com/palmate" rel="noopener noreferrer" target="_blank">
              {t("common.clickHere")}
            </PalLink>
          </PalTypography>
        </Grid>

        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            {t("dashboard.onboarding.selectShopifyIntegrationLabel")}
          </PalTypography>
          <PalAutoComplete
            fullWidth
            disabled={formik.values[stepKey]?.integrated}
            invalid={formik.touched[stepKey]?.shopify_integration && formik.errors[stepKey]?.shopify_integration}
            isClearable={false}
            options={integrationOptions}
            placeholder={t("dashboard.onboarding.selectShopifyIntegrationPlaceholder")}
            value={formik.values[stepKey]?.shopify_integration}
            onChange={handleChangeIntegration}
          />
        </Grid>
      </Grid>
    </StepContainer>
  );
}

export default StepShopifyIntegration;
